import { useState } from 'react';
import { Button, Modal, message } from 'antd';
import type { IntegrationsApi } from './types';

/**
 * Probes a stored CRM connection. Lifted (controller ruling, p6 Task 2) from
 * the console's `TestConnectionButton` — minus its privileged, app-scoped
 * network-call import, which arrives here as the `test` prop instead
 * (closure-only: `packages/*` never fetches or names an endpoint, see
 * docs/feature-authoring.md).
 *
 * The backend returns `{ status: 'ok' | 'error'; message }` (NOT `{ ok }`):
 *  - status 'ok'    → success toast + `onSuccess` (e.g. to refresh capability).
 *  - status 'error' → an error modal with the backend message.
 *  - a thrown error (network / 5xx) → an error modal with the caught message.
 *
 * `runConnectionTest` is the button-less core, shared with the connections
 * table's row-kebab Test action (`ConnectionsTable`) so both surfaces report
 * results identically. Capability gating happens at the call site (this
 * button itself is unguarded).
 */
export async function runConnectionTest(
  test: IntegrationsApi['testConnection'],
  connectionId: string,
  onSuccess?: () => void,
): Promise<void> {
  try {
    const result = await test(connectionId);
    if (result.status === 'ok') {
      message.success(result.message || 'Connection successful');
      onSuccess?.();
    } else {
      Modal.error({
        title: 'Connection failed',
        content: result.message || 'Unknown error',
      });
    }
  } catch (err: unknown) {
    Modal.error({
      title: 'Connection test error',
      content:
        err instanceof Error
          ? err.message
          : ((err as { message?: string })?.message ?? 'Test failed'),
    });
  }
}

export interface TestConnectionButtonProps {
  connectionId: string;
  test: IntegrationsApi['testConnection'];
  onSuccess?: () => void;
}

export function TestConnectionButton({
  connectionId,
  test,
  onSuccess,
}: TestConnectionButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleTest = async () => {
    setLoading(true);
    try {
      await runConnectionTest(test, connectionId, onSuccess);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button onClick={handleTest} loading={loading}>
      Test Connection
    </Button>
  );
}
